<template>
  <v-container class="fill-height text-start" style="opacity: 0.94" fluid>
    <v-sheet elevation="1" rounded width="100%" class="mt-16 mb-8">
      <deprecation-banner />
      <v-toolbar rounded class="mb-5">
        <div class="d-flex justify-space-between" style="width: 100%">
          <h2>Custom Print Extractor</h2>
          <v-btn :loading="loading" @click="extractPrint" color="primary">
            <template #loader>
              <span class="custom-loader">
                <v-icon light>mdi-cached</v-icon>
              </span>
            </template>
            Extract
          </v-btn>
        </div>
      </v-toolbar>
      <v-row justify="space-around" class="py-5 px-12">
        <v-col cols="4" class="text-end">
          <v-form>
            <v-tabs v-model="selectedTab" class="mb-3">
              <v-tab
                v-for="tab in tabs"
                :key="tab.value"
                style="text-transform: none"
                :value="tab.value"
              >
                {{ tab.label }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="selectedTab">
              <v-tab-item>
                <v-list class="mb-5" outlined nav>
                  <v-list-item-group v-model="printType" color="primary">
                    <v-row justify="start" class="flex-column">
                      <v-col cols="12" class="p-2">
                        <v-list-item
                          v-for="type in printTypes"
                          :key="type.id"
                          :value="type.value"
                          class="p-16-0"
                        >
                          {{ type.label }}
                        </v-list-item>
                      </v-col>
                    </v-row>
                  </v-list-item-group>
                </v-list>
              </v-tab-item>
              <v-tab-item>
                <v-textarea outlined v-model="prompt" rows="8"></v-textarea>
                <v-list class="mb-5" outlined nav>
                  <v-list-item-group v-model="model" color="primary">
                    <v-row justify="start" class="flex-column">
                      <v-col cols="12" class="p-2">
                        <v-list-item
                          v-for="model in models"
                          :key="model.id"
                          :value="model.value"
                          class="p-16-0"
                        >
                          {{ model.label }}
                        </v-list-item>
                      </v-col>
                    </v-row>
                  </v-list-item-group>
                </v-list>
              </v-tab-item>
            </v-tabs-items>
          </v-form>
        </v-col>
        <v-col cols="8">
          <v-list outlined nav>
            <v-subheader style="font-size: 1rem; font-weight: 500"
              >Preview {{ responseTime && `(${responseTime})` }}</v-subheader
            >
            <v-row>
              <v-col cols="6">
                <image-upload @change="handleImageSelected" />
              </v-col>
              <v-col cols="6" style="position: relative">
                <v-btn
                  fab
                  style="position: absolute; z-index: 9999; right: 20px; top: 20px"
                  small
                  @click="downloadImage"
                >
                  <v-icon>mdi-cloud-download-outline</v-icon>
                </v-btn>
                <v-img :src="previewSrc" />
              </v-col>
            </v-row>
          </v-list>
        </v-col>
      </v-row>
      <div class="px-5" v-if="!!edits.length">
        <h4
          style="
            font-weight: 600;
            font-size: 1.5rem;
            margin-left: 1rem;
            text-decoration: underline;
            text-underline-offset: 0.5rem;
          "
        >
          Extractions:
        </h4>
        <div style="display: flex; margin-bottom: 100px">
          <v-img
            v-for="edit in edits"
            :key="edit"
            @click="selectEdit(edit)"
            :src="edit"
            :style="`object-fit: cover; margin: 1rem; width: 15%; cursor: pointer;`"
          />
        </div>
      </div>
      <div class="px-5" v-if="!!originalGeminiEdits.length">
        <h4
          style="
            font-weight: 600;
            font-size: 1.5rem;
            margin-left: 1rem;
            text-decoration: underline;
            text-underline-offset: 0.5rem;
          "
        >
          Original Gemini Edits:
        </h4>
        <div style="display: flex; margin-bottom: 100px">
          <v-img
            v-for="edit in originalGeminiEdits"
            :key="edit"
            @click="selectEdit(edit)"
            :src="edit"
            :style="`object-fit: cover; margin: 1rem; width: 15%; cursor: pointer;`"
          />
        </div>
      </div>
    </v-sheet>
  </v-container>
</template>

<script>
  import ImageUpload from '../components/ImageUpload.vue'
  import DeprecationBanner from '../components/DeprecationBanner.vue'
  import prompts from '@/assets/mizodia/custom-print-prompts.json'
  import { generateEdits, generateEditsAndProcess } from '../services/pdfGenerationService'
  const EDITS_PER_REQUEST = 3

  export default {
    props: {
      source: String,
    },
    data() {
      return {
        previewOriginal: 'https://placehold.co/200x300',
        previewSrc: 'https://placehold.co/200x300',
        loading: false,
        selectedImage: null,
        edits: [],
        originalGeminiEdits: [],
        selectedTab: 0,
        tabs: [
          { label: 'Predefined', value: 0 }, // 'predefined'
          { label: 'Custom Prompt', value: 1 }, // 'custom_prompt'
        ],
        printType: 'fingerprint',
        prompt: prompts['fingerprint'],
        printTypes: [
          { label: 'Fingerprint', value: 'fingerprint' },
          { label: 'Nose Print', value: 'pet_nose_print' },
          { label: 'Paw Print', value: 'pet_paw_print' },
          { label: 'Baby Hands', value: 'baby_hands' },
          { label: 'Baby Feet', value: 'baby_feet' },
          { label: 'Handwriting', value: 'handwriting' },
        ],
        model: 'gemini-2.5-flash-image',
        models: [
          { label: 'Gemini 2.5', value: 'gemini-2.5-flash-image' },
          { label: 'Gemini 3', value: 'gemini-3-pro-image-preview' },
        ],
        responseTime: null,
      }
    },
    methods: {
      selectEdit(edit) {
        this.previewSrc = edit
      },
      handleImageSelected(file) {
        if (file) {
          this.selectedImage = file
          const reader = new FileReader()
          reader.onload = (e) => {
            this.previewOriginal = e.target.result
          }
          reader.readAsDataURL(file)
        }
      },
      async extractPrint() {
        this.loading = true
        try {
          const reader = new FileReader()
          const fileLoaded = new Promise((resolve, reject) => {
            reader.onloadend = () => {
              if (reader.result) {
                const result = reader.result
                resolve(result.split(',')[1])
              } else reject('Failed to read file')
            }
          })
          reader.readAsDataURL(this.selectedImage)
          const base64Image = await fileLoaded
          const predefinedTab = this.selectedTab === 0
          const model = this.model
          let prompt = predefinedTab ? prompts[this.printType] : this.prompt
          prompt +=
            model === this.models[0].value
              ? `Generate ${EDITS_PER_REQUEST} different edits for this.`
              : `You must generate ${EDITS_PER_REQUEST} edited versions of the input image, return exactly ${EDITS_PER_REQUEST} separate response parts, each containing only one edited image with no text, no combined images, no grouping; the output must be a sequence of ${EDITS_PER_REQUEST} independent image parts, and you must not put multiple images in one part or produce a collage.`
          const fetchEdits = ['baby_feet'].includes(this.printType)
            ? generateEditsAndProcess
            : generateEdits

          let type = (this.selectedImage.type || this.selectedImage.name?.split('.').slice(-1)[0])?.toLowerCase()
          type = type.startsWith('image/') ? type : `image/${type}`
          
          const response = await fetchEdits(model, prompt, base64Image, type, null)
          console.log('response:', response)

          const data = response?.data
          if (data?.success) {
            console.log(data.data.edits)
            const edits = data.data.edits.map((edit) => edit.url)
            this.previewSrc = edits?.[0]
            this.edits = edits
            this.originalGeminiEdits = (data.data.originalGeminiEdits || []).map((edit) => edit.url)
            this.responseTime =
              (parseInt(data.responseTime.replace('ms', '')) / 1000).toFixed(2) + 's'
          }
        } catch (err) {
          console.log(err)
        } finally {
          this.loading = false
          const audio = new Audio(
            'https://actions.google.com/sounds/v1/cartoon/wood_plank_flicks.ogg'
          )
          audio.play() // to notify the distracted user that your async request is complete
        }
      },

      async downloadImage() {
        const link = await this.linkToObjectUrl(this.previewSrc)
        // Create a download link
        const downloadLink = document.createElement('a')
        downloadLink.href = link
        downloadLink.download = `${this.printType}.png`
        downloadLink.click()
      },
      async linkToObjectUrl(link) {
        const response = await fetch(link)
        const blob = await response.blob()
        return URL.createObjectURL(blob)
      },
    },

    watch: {
      printType() {
        this.prompt = prompts[this.printType]
      },
    },
    components: { ImageUpload, DeprecationBanner },
  }
</script>

<style scoped>
  .centered-input >>> input {
    text-align: center;
  }

  .custom-loader {
    transform: scaleX(-1);
    -moz-transform: scaleX(-1);
    -webkit-transform: scaleX(-1);
    -ms-transform: scaleX(-1);
    animation: loader 1s infinite;
    display: flex;
  }

  @-moz-keyframes loader {
    from {
      transform: rotate(0);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @-webkit-keyframes loader {
    from {
      transform: rotate(0);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @-o-keyframes loader {
    from {
      transform: rotate(0);
    }

    to {
      transform: rotate(360deg);
    }
  }

  @keyframes loader {
    from {
      transform: rotate(0);
    }

    to {
      transform: rotate(360deg);
    }
  }
</style>
